import React from "react";
import { Button, Grid } from "@material-ui/core";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { Controller, useWatch } from "react-hook-form";
import { News } from "../../domain/News";
import { SectionProps } from "./SectionProps";
import { Error } from "../common/Error";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { DateTimePicker } from "@ofqual/portal-components/components/DateTimePicker";
import { Select } from "@ofqual/portal-components/components/Select";
import { toGroupedList } from "@ofqual/portal-core/core/utils/listHelpers";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { newsService, newsTypeService, tagsService } from "../../services";
import { ItemType } from "@ofqual/portal-components/components/types";
import { fieldLabels } from "./fieldLabels";
import { AttachmentList } from "../common/AttachmentList";
import { useDispatch, useSelector } from "react-redux";
import { authSelector } from "../../redux/selectors";
import { Attachment } from "../../domain/Attachment";
import { deleteSelectedAttachment } from "../../redux/actions";
import { HtmlEditor } from "../common/HtmlEditor";

export const NewsDetails: React.FC<SectionProps<News>> = ({
  control,
  errors,
  disabled,
  item,
  lookupTracker,
  setValue,
  getValues,
}) => {
  const dispatch = useDispatch();
  const { token } = useSelector(authSelector);

  const [tagsLookups, newsTypeLookups] = useLookup(
    {
      service: tagsService,
      id: "id",
      label: "tag",
    },
    newsTypeService
  );

  const groupedTagsLookup = toGroupedList(tagsLookups, {
    id: "id",
    label: "tag",
    group: "tagCategory",
  });

  const deleteAttachment = (attachment: Attachment) =>
    dispatch(deleteSelectedAttachment(attachment));

  const buildGetBlob = (blobId: string): Promise<Blob> =>
    newsService.downloadAttachment(item!.id, blobId, { token });

  const { image } = useWatch({
    control,
    name: ["image"],
  });

  // Brian N.   Date format is wrong its a malformed UTC string as it missing a Z at the end
  // we have an API fix but then the date helper would get the time wrong and its used everywhere
  // this is really ugly... I am so sorry :-(
  var val = getValues("publishedDate");
  if (val) {
    const regex = "(Z)$";
    var found = val.match(regex);
    if (!found) {
      setValue("publishedDate", val + "Z");
    }
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="type"
          rules={{ required: true }}
          as={
            <Select
              label={fieldLabels.type}
              readOnly={disabled}
              datatest="news-type"
              lookupTracker={lookupTracker}
              required
            >
              {newsTypeLookups}
            </Select>
          }
        />
        {errors.type?.type === "required" && (
          <Error>This field is required</Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="title"
          rules={{ required: true }}
          as={
            <TextField
              label={fieldLabels.title}
              data-test="news-title"
              fullWidth
              readOnly={disabled}
              required
            />
          }
        />
        {errors.title?.type === "required" && (
          <Error>This field is required</Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="subHeading"
          as={
            <TextField
              label={fieldLabels.subHeading}
              data-test="sub-heading"
              fullWidth
              readOnly={disabled}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="excerpt"
          as={
            <TextField
              label={fieldLabels.excerpt}
              data-test="excerpt-heading"
              fullWidth
              readOnly={disabled}
              multiline
              rows={4}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="content"
          render={({ onChange, value }) => (
            <>
              <label>Message {disabled ? "(Read Only)" : ""} </label>
              <HtmlEditor
                onChange={onChange}
                value={value}
                readOnly={disabled}
              />
            </>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          control={control}
          name="publishedDate"
          as={
            <DateTimePicker
              dataTest="published-date"
              label={fieldLabels.publishedDate}
              required
            />
          }
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          control={control}
          name="resourceTags"
          as={
            <Select
              selectVariant="checkboxes"
              label={fieldLabels.resourceTags}
              datatest="tags"
              readOnly={disabled}
              lookupTracker={lookupTracker}
            >
              {groupedTagsLookup as ItemType[]}
            </Select>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="files"
          defaultValue={[]}
          as={<FileInput multiple={true} disabled={disabled} />}
        />
      </Grid>
      {item?.newsItemAttachments ? (
        <AttachmentList
          canEdit={!disabled}
          attachments={item.newsItemAttachments}
          deleteAttachment={deleteAttachment}
          getBlob={buildGetBlob}
        />
      ) : null}

      {item?.imageSrc && image && !image.length ? (
        <Grid item xs={12}>
          <img src={item.imageSrc} alt="news" style={{ maxWidth: "75%" }}></img>
        </Grid>
      ) : null}

      <Grid item xs={12}>
        <Controller
          control={control}
          name="image"
          defaultValue={[]}
          as={
            <FileInput
              disabled={disabled}
              title="Upload image"
              accept="image/*"
            />
          }
        />
      </Grid>
      {item?.videoEmbed ? (
        <Grid item xs={12}>
          {/* TODO: find a better way to do this, dangerouslySetInnerHTML is bad! */}
          <div dangerouslySetInnerHTML={{ __html: item.videoEmbed }} />
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <Controller
          control={control}
          name="videoEmbed"
          as={
            <TextField
              label={fieldLabels.videoEmbed}
              data-test="video-embed"
              fullWidth
              readOnly={disabled}
              rows={4}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Button
          color="primary"
          onClick={() =>
            window.open(
              "https://officequal.sharepoint.com/portals/hub/_layouts/15/PointPublishing.aspx?app=video&p=u"
            )
          }
        >
          Upload video to Office 365.
        </Button>
      </Grid>
    </Grid>
  );
};
